import { Injectable, signal } from '@angular/core';
import { GameItem } from '../models/game-item.models';
import { computeItemHash } from '../utils/item-hash';
import { poe1 } from '../../game';

const prefix = poe1 ? 'poe1' : 'poe';
const KEY_ITEM_LIBRARY = `${prefix}-item-library`;
const MAX_ITEMS = 100;

export interface ItemLibraryRecord {
  hash: string;
  item: GameItem;
  itemText?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ItemLibraryService {
  readonly items = signal<ItemLibraryRecord[]>([]);

  constructor() {
    this.load();
  }

  async add(item: GameItem, itemText: string): Promise<void> {
    const hash = await computeItemHash(itemText, JSON.stringify(item));
    if (this.items().some(r => r.hash === hash)) {
      return;
    }
    const next = [{ hash, item: new GameItem(item), itemText }, ...this.items()];
    const capped = next.length > MAX_ITEMS ? next.slice(0, MAX_ITEMS) : next;
    this.persist(capped);
  }

  removeByHash(hash: string): void {
    this.persist(this.items().filter(r => r.hash !== hash));
  }

  clearAll(): void {
    this.items.set([]);
    localStorage.removeItem(KEY_ITEM_LIBRARY);
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(KEY_ITEM_LIBRARY);
      if (!raw) {
        this.items.set([]);
        return;
      }
      const parsed = JSON.parse(raw) as ItemLibraryRecord[];
      this.items.set(
        parsed.map(r => ({
          hash: r.hash,
          item: new GameItem(r.item),
          itemText: r.itemText,
        }))
      );
    } catch {
      this.items.set([]);
    }
  }

  private persist(records: ItemLibraryRecord[]): void {
    this.items.set(records);
    localStorage.setItem(KEY_ITEM_LIBRARY, JSON.stringify(records));
  }
}
